import React from "react";
import PropTypes from "prop-types";
import {
  IoCheckmarkCircle,
  IoRemoveCircle,
  IoDocumentText
} from "react-icons/io5";

import ReactTable from "react-table";

import ResponsiveDataComponent from "../common/ResponsiveDataComponent";
import Loading from "../common/Loading";
import TableActions from "../common/TableActions";
import ClickableTooltip from "../common/ClickableTooltip";

import { formatDate } from "../../helpers/date";

const ContractList = ({
  onPageSizeChange,
  onPageChange,
  onSortedChange,
  sorted,
  page,
  pageSize,
  contracts,
  loading,
  isMobile,
  onClickView,
  onClickFullView,
  auth,
  total,
  pages,
  manual
}) => {
  const columns = !isMobile
    ? [
        { Header: "ID", accessor: "number", maxWidth: 100 },
        { Header: "Name", accessor: "name", minWidth: 180 },
        { Header: "Contract Type", accessor: "contract_type.label" },
        { Header: "Customer", accessor: "customer.name" },
        {
          Header: "Entered Date",
          accessor: "entered_date",
          maxWidth: 120,
          Cell: (row) => formatDate(row.value, "MM/dd/yyyy")
        },
        {
          Header: "Start Date",
          accessor: "start_date",
          maxWidth: 120,
          Cell: (row) =>
            row.value ? formatDate(row.value, "MM/dd/yyyy") : "--"
        },
        {
          Header: "End Date",
          accessor: "end_date",
          maxWidth: 120,
          Cell: (row) =>
            row.value ? formatDate(row.value, "MM/dd/yyyy") : "--"
        },
        {
          Header: "Status",
          accessor: "contract_status.label",
          maxWidth: 120,
          Cell: (row) => {
            const icon =
              row.value === "Active" ? (
                <IoCheckmarkCircle />
              ) : (
                <IoRemoveCircle />
              );
            const className = row.value === "Active" ? "active" : "inactive";
            return (
              <span className={className}>
                {icon} {row.value}
              </span>
            );
          }
        },
        {
          accessor: "service_note",
          maxWidth: 40,
          Cell: (row) =>
            row.value ? (
              <ClickableTooltip
                custom
                id={row.original._id}
                isNightMode={auth.isNightMode}
                loading={loading}
                tooltipContent={
                  <React.Fragment>
                    <b>Service Note: </b>
                    {row.value}
                  </React.Fragment>
                }
              >
                <IoDocumentText style={{ color: "#50accb" }} />
              </ClickableTooltip>
            ) : null
        }
      ]
    : [
        {
          Header: "Name",
          accessor: "name"
        }
      ];

  columns.push({
    accessor: "_id",
    maxWidth: 80,
    sortable: false,
    Cell: (row) =>
      onClickFullView ? (
        <TableActions
          onClickView={() => onClickView(row.value)}
          onClickFullView={() => onClickFullView(row.value)}
          viewText="Quick View"
        />
      ) : (
        <TableActions onClickView={() => onClickView(row.value)} />
      )
  });

  const serverSideProps = {};

  if (manual) {
    serverSideProps.page = page;
  }

  return (
    <ReactTable
      {...serverSideProps}
      manual={manual}
      getTdProps={(state, rowInfo, column) => {
        if (!rowInfo && column.id === "service_note") {
          return {
            style: {
              textOverflow: "unset"
            }
          };
        }
        if (!column.expander) {
          return {
            onClick: () => {
              if (
                column.id !== "_id" &&
                column.id !== "service_note" &&
                rowInfo?.original?._id
              ) {
                onClickView(rowInfo.original._id);
              }
            }
          };
        }
        return {};
      }}
      className="-highlight"
      data={contracts}
      columns={columns}
      showPagination={total > 5 || contracts.length > 5}
      noDataText="No contracts found"
      showPageSizeOptions={!isMobile}
      pageSize={pageSize}
      LoadingComponent={() => <Loading active={loading} />}
      SubComponent={
        isMobile
          ? ({ original }) => (
              <ResponsiveDataComponent
                data={[
                  { key: "ID", label: original.number },
                  { key: "Name", label: original.name },
                  {
                    key: "Contract Type",
                    label: original.contract_type.label
                  },
                  { key: "Customer", label: original.customer.name },
                  {
                    key: "Entered Date",
                    label: formatDate(original.entered_date, "MM/dd/yyyy")
                  },
                  {
                    key: "Status",
                    label: original.contract_status.label
                  }
                ]}
              />
            )
          : false
      }
      pages={pages}
      sorted={sorted}
      onPageSizeChange={onPageSizeChange}
      onPageChange={onPageChange}
      onSortedChange={onSortedChange}
    />
  );
};

ContractList.propTypes = {
  manual: PropTypes.bool,
  contracts: PropTypes.array.isRequired,
  auth: PropTypes.object,
  onClickView: PropTypes.func,
  isMobile: PropTypes.bool,
  onClickFullView: PropTypes.func,
  total: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  onPageChange: PropTypes.func,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  loading: PropTypes.bool,
  onSortedChange: PropTypes.func,
  sorted: PropTypes.array,
  pages: PropTypes.number
};

ContractList.defaultProps = {
  onPageSizeChange: () => null,
  onPageChange: () => null,
  onSortedChange: () => null,
  sorted: [{ id: "name", desc: 1 }]
};

export default ContractList;
